import React, { useState, useEffect, useCallback } from "react";
import { useTheme, makeStyles } from "@material-ui/core/styles";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  MenuItem,
  Select,
  FormControl,
  Typography,
  CircularProgress,
  InputLabel,
  Box,
} from "@material-ui/core";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  eachDayOfInterval,
  subMonths,
  subDays,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(1.5),
    flexWrap: "wrap",
    gap: 8,
  },
  title: {
    fontWeight: "bold",
    fontSize: "1rem",
    color: theme.palette.text.primary,
  },
  subtitle: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  controls: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 200,
  },
  empty: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: 200,
    color: theme.palette.text.secondary,
    gap: 8,
  },
}));

const PERIODS = [
  { label: "Últimos 7 dias", value: "last7" },
  { label: "Últimos 30 dias", value: "last30" },
  { label: "Este mês", value: "thisMonth" },
  { label: "Mês anterior", value: "lastMonth" },
];

const STATUS_COLORS = [
  "#6366f1", "#f59e0b", "#10b981", "#ef4444", "#3b82f6",
  "#ec4899", "#14b8a6", "#f97316", "#8b5cf6", "#06b6d4",
];

function getDateRange(period) {
  const now = new Date();
  switch (period) {
    case "last7":
      return { start: startOfDay(subDays(now, 6)), end: endOfDay(now) };
    case "last30":
      return { start: startOfDay(subDays(now, 29)), end: endOfDay(now) };
    case "thisMonth":
      return { start: startOfMonth(now), end: endOfMonth(now) };
    case "lastMonth": {
      const last = subMonths(now, 1);
      return { start: startOfMonth(last), end: endOfMonth(last) };
    }
    default:
      return { start: startOfDay(subDays(now, 6)), end: endOfDay(now) };
  }
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{
      background: "#1e293b",
      border: "1px solid #334155",
      borderRadius: 8,
      padding: "10px 14px",
      color: "#f1f5f9",
      fontSize: "0.8rem",
      minWidth: 160,
    }}>
      <div style={{ fontWeight: "bold", marginBottom: 6, borderBottom: "1px solid #334155", paddingBottom: 4 }}>
        📅 {label}
      </div>
      {payload.map((entry) => (
        <div key={entry.dataKey} style={{ display: "flex", justifyContent: "space-between", gap: 16, marginTop: 3 }}>
          <span style={{ color: entry.color }}>● {entry.name}</span>
          <strong>{entry.value}</strong>
        </div>
      ))}
    </div>
  );
};

const MonthlyChart = ({ whatsappId = "", startDate: externalStartDate, endDate: externalEndDate, periodLabel: externalPeriodLabel }) => {
  const theme = useTheme();
  const classes = useStyles();

  const [period, setPeriod] = useState("last7");
  const [connections, setConnections] = useState([]);

  const [chartData, setChartData] = useState([]);
  const [statusKeys, setStatusKeys] = useState([]);
  const [visibleKeys, setVisibleKeys] = useState(new Set());
  const [loading, setLoading] = useState(false);

  // Helper para verificar se a chave é padrão (Novos Contatos, Qualificados ou Agendados)
  const isDefaultKey = (name) => {
    if (!name) return false;
    const lower = name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    return (
      lower.includes("novos contatos") ||
      lower.includes("qualificad") ||
      lower.includes("agendad")
    );
  };

  // Load connections list
  useEffect(() => {
    api.get("/whatsapp").then(({ data }) => {
      setConnections(data || []);
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let start, end, startDateStr, endDateStr;
      if (externalStartDate && externalEndDate) {
        startDateStr = externalStartDate;
        endDateStr = externalEndDate;
        const [sy, sm, sd] = externalStartDate.split("-").map(Number);
        const [ey, em, ed] = externalEndDate.split("-").map(Number);
        start = new Date(sy, sm - 1, sd);
        end = new Date(ey, em - 1, ed);
      } else {
        const range = getDateRange(period);
        start = range.start;
        end = range.end;
        startDateStr = format(start, "yyyy-MM-dd");
        endDateStr = format(end, "yyyy-MM-dd");
      }

      const params = { startDate: startDateStr, endDate: endDateStr, showAll: "true" };
      if (whatsappId) params.whatsappId = whatsappId;

      const [{ data: rows }, { data: ticketsData }] = await Promise.all([
        api.get("/contacts/status-history", { params }).catch(() => ({ data: [] })),
        api.get("/tickets", { params }).catch(() => ({ data: { tickets: [] } }))
      ]);

      const ticketsList = ticketsData?.tickets || [];

      // Build day-indexed map
      const days = eachDayOfInterval({ start, end });
      const dayMap = {};
      days.forEach((d) => {
        dayMap[format(d, "yyyy-MM-dd")] = { "Novos Contatos": 0 };
      });

      // Count tickets per day
      ticketsList.forEach((t) => {
        if (t.createdAt) {
          const dayKey = format(new Date(t.createdAt), "yyyy-MM-dd");
          if (dayMap[dayKey]) {
            dayMap[dayKey]["Novos Contatos"]++;
          }
        }
      });

      const allStatuses = new Set(["Novos Contatos"]);
      (rows || []).forEach((row) => {
        const day = row.day ? String(row.day).slice(0, 10) : null;
        if (day && dayMap[day] !== undefined) {
          dayMap[day][row.statusName] = Number(row.count);
          allStatuses.add(row.statusName);
        }
      });

      const keys = Array.from(allStatuses);
      setStatusKeys(keys);

      // Inicia habilitado APENAS Novos Contatos, Qualificados e Agendados
      setVisibleKeys((prev) => {
        // Se o usuário já interagiu e ativou/desativou linhas na sessão atual, preserva
        if (prev && prev.size > 0) {
          return prev;
        }
        const defaultVisible = new Set();
        keys.forEach((k) => {
          if (isDefaultKey(k)) {
            defaultVisible.add(k);
          }
        });
        // Garantia: se por algum motivo nenhuma bateu, ativa ao menos 'Novos Contatos'
        if (defaultVisible.size === 0 && keys.length > 0) {
          defaultVisible.add(keys[0]);
        }
        return defaultVisible;
      });

      const built = days.map((d) => {
        const key = format(d, "yyyy-MM-dd");
        const label = format(d, "dd/MM", { locale: ptBR });
        const entry = { label };
        keys.forEach((s) => {
          entry[s] = dayMap[key][s] || 0;
        });
        return entry;
      });

      setChartData(built);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [period, whatsappId, externalStartDate, externalEndDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selectedPeriodLabel = externalPeriodLabel || PERIODS.find((p) => p.value === period)?.label || "";
  const selectedConn = connections.find((c) => String(c.id) === String(whatsappId));
  const hasData = chartData.length > 0;

  return (
    <>
      <div className={classes.header}>
        <div>
          <Typography className={classes.title}>
            📈 Novos Contatos por Dia — {selectedPeriodLabel}
            {selectedConn ? ` · ${selectedConn.name}` : ""}
          </Typography>
          <Typography className={classes.subtitle}>
            Iniciando com <strong>Novos Contatos</strong>, <strong>Qualificados</strong> e <strong>Agendados</strong>. Clique na legenda abaixo para ativar ou ocultar mais métricas.
          </Typography>
        </div>

        <div className={classes.controls}>
          {/* Mostra seletor interno apenas se não estiver recebendo período externo */}
          {!externalStartDate && (
            <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
              <InputLabel>Período</InputLabel>
              <Select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                label="Período"
              >
                {PERIODS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
      </div>

      {loading ? (
        <div className={classes.loading}>
          <CircularProgress size={32} />
        </div>
      ) : !hasData ? (
        <div className={classes.empty}>
          <span style={{ fontSize: "2rem" }}>📊</span>
          <Typography variant="body2">
            Nenhuma mudança de status neste período.
          </Typography>
          <Typography variant="caption">
            Os dados aparecem conforme você atualiza os status dos contatos.
          </Typography>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart
            data={chartData}
            margin={{ top: 6, right: 16, bottom: 0, left: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis
              dataKey="label"
              stroke={theme.palette.text.secondary}
              tick={{ fontSize: 11 }}
              interval={chartData.length > 14 ? Math.floor(chartData.length / 10) : 0}
            />
            <YAxis
              stroke={theme.palette.text.secondary}
              tick={{ fontSize: 11 }}
              allowDecimals={false}
              width={28}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: "0.78rem", paddingTop: 8, cursor: "pointer", userSelect: "none" }}
              onClick={(e) => {
                const clickedKey = e?.dataKey;
                if (!clickedKey) return;
                setVisibleKeys((prev) => {
                  const next = new Set(prev);
                  if (next.has(clickedKey)) {
                    // Impede desmarcar todas (mantém pelo menos 1 ativa)
                    if (next.size > 1) {
                      next.delete(clickedKey);
                    }
                  } else {
                    next.add(clickedKey);
                  }
                  return next;
                });
              }}
            />
            {statusKeys.map((statusName, i) => {
              const isVisible = visibleKeys.has(statusName);
              return (
                <Line
                  key={statusName}
                  type="monotone"
                  dataKey={statusName}
                  name={statusName}
                  stroke={STATUS_COLORS[i % STATUS_COLORS.length]}
                  strokeWidth={isVisible ? 2 : 0}
                  dot={isVisible ? { r: 3 } : false}
                  activeDot={isVisible ? { r: 5 } : false}
                  hide={!isVisible}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      )}
    </>
  );
};

export default MonthlyChart;
